import { useEffect, useRef, useState } from "react";
import Lenis from "lenis";
import "lenis/dist/lenis.css";
import { RepairHeroPhoto, RepairShowcase } from "../components/RepairShowcase";
import { Link } from "react-router-dom";
import {
    Smartphone,
    Battery,
    Droplets,
    Plug,
    Camera,
    Cpu,
    ShieldCheck,
    BadgeCheck,
    Zap,
    ArrowRight,
    Wrench,
    Star,
    MapPin,
    Phone,
    Clock,
    MessageCircle,
    Menu,
    X,
} from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../components/ui/accordion";
import { api, formatApiErrorDetail } from "../lib/api";

const SCREEN_IMG = "/images/repairs/phone-parts.webp";
const INSPECTION_IMG = "/images/repairs/phone-inspection.webp";

const SERVICES = [
    { icon: Smartphone, title: "Screen Replacement", time: "30 min", desc: "Cracked or unresponsive displays — OEM-grade panels for iPhone, Samsung, Pixel & more." },
    { icon: Battery, title: "Battery Replacement", time: "20 min", desc: "Restore all-day life. Genuine cells with health diagnostics included." },
    { icon: Droplets, title: "Water Damage", time: "24-48 hr", desc: "Ultrasonic cleaning + corrosion treatment with no-fix-no-fee promise." },
    { icon: Plug, title: "Charging Port", time: "45 min", desc: "Loose, intermittent or dead ports rebuilt with new flex assembly." },
    { icon: Camera, title: "Camera Module", time: "1 hr", desc: "Front & rear lenses, sensors, autofocus & glass cover fixes." },
    { icon: Cpu, title: "Software / Boot Loop", time: "1 hr", desc: "iOS / Android recovery, IMEI repair, FRP unlock, system flashing." },
];

const BRANDS = ["Apple", "Samsung", "Google", "OnePlus", "Xiaomi", "Huawei", "Motorola", "Sony"];

const TESTIMONIALS = [
    { name: "Aiden M.", text: "Walked in with a shattered iPhone 14 Pro screen. Out in 28 minutes — looks brand new.", role: "Architect" },
    { name: "Priya S.", text: "Battery on my S22 was dead by noon. Mr Mobile installed a new one same day. Lasts all weekend now.", role: "Photographer" },
    { name: "Marcus T.", text: "Dropped my Pixel in a pool. Wrote it off. They brought it back to life. Genuinely impressed.", role: "Designer" },
];

const FAQS = [
    { q: "Do you offer a warranty on repairs?", a: "Yes — every repair is backed by a 90-day parts & labour warranty. Screen and battery jobs include a lifetime guarantee against defects." },
    { q: "How long does a typical repair take?", a: "Most screen and battery jobs are completed in under 45 minutes while you wait. Complex board-level and water damage repairs can take 24–72 hours." },
    { q: "Are your parts genuine?", a: "We use OEM-grade or original manufacturer parts for all major brands. We will clearly tell you which grade is used before starting any repair." },
    { q: "What if you can't fix my device?", a: "No fix, no fee. If we can't repair your device we don't charge a diagnostic fee — and you get it back exactly as you brought it in." },
    { q: "Do you offer mail-in repairs?", a: "Yes. Request a quote, ship your device via our prepaid label, we repair within 48 hours, then express ship it back." },
];

export default function HomePage() {
    const reduceMotion = useReducedMotion();
    const lenisRef = useRef(null);
    const [mobileOpen, setMobileOpen] = useState(false);

    useEffect(() => {
        if (reduceMotion) return;
        const lenis = new Lenis({ autoRaf: true, duration: 1.05, smoothWheel: true, syncTouch: false, prevent: (node) => node.closest?.('[role="dialog"], [role="listbox"]') });
        lenisRef.current = lenis;
        return () => { lenis.destroy(); lenisRef.current = null; };
    }, [reduceMotion]);
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        device: "",
        issue: "",
        description: "",
    });
    const [submitting, setSubmitting] = useState(false);

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/quotes", form);
            toast.success("Quote request received!", {
                description: "A specialist will reach out within 30 minutes.",
            });
            setForm({ name: "", email: "", phone: "", device: "", issue: "", description: "" });
        } catch (err) {
            toast.error("Could not send", {
                description: formatApiErrorDetail(err?.response?.data?.detail) || err.message,
            });
        } finally {
            setSubmitting(false);
        }
    };

    const scrollTo = (id) => () => {
        const target = document.getElementById(id);
        if (target && lenisRef.current) {
            lenisRef.current.scrollTo(target, { offset: -90 });
        } else {
            target?.scrollIntoView({ behavior: reduceMotion ? "instant" : "smooth", block: "start" });
        }
        setMobileOpen(false);
    };

    return (
        <div className="min-h-screen bg-[#050505] text-white pb-16 md:pb-0" data-testid="home-page">
            {/* Header */}
            <header
                data-testid="site-header"
                className="fixed top-0 left-0 right-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/10"
            >
                <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
                    <button
                        data-testid="logo-link"
                        onClick={scrollTo("hero")}
                        className="flex items-center gap-2 font-display font-bold text-lg md:text-xl"
                    >
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-sm bg-[#00E5FF] text-black">
                            <Wrench className="h-4 w-4" />
                        </span>
                        Mr Mobile
                    </button>
                    <nav className="hidden lg:flex items-center gap-6 text-sm text-zinc-300">
                        <button data-testid="nav-services" onClick={scrollTo("services")} className="hover:text-white transition-colors">Services</button>
                        <button data-testid="nav-gallery" onClick={scrollTo("repair-gallery")} className="hover:text-white transition-colors">Repair Gallery</button>
                        <button data-testid="nav-why" onClick={scrollTo("why")} className="hover:text-white transition-colors">Why Us</button>
                        <button data-testid="nav-reviews" onClick={scrollTo("reviews")} className="hover:text-white transition-colors">Reviews</button>
                        <button data-testid="nav-faq" onClick={scrollTo("faq")} className="hover:text-white transition-colors">FAQ</button>
                        <button data-testid="nav-contact" onClick={scrollTo("contact")} className="hover:text-white transition-colors">Contact</button>
                    </nav>
                    <div className="flex items-center gap-3">
                        <button
                            data-testid="header-quote-cta"
                            onClick={scrollTo("contact")}
                            className="hidden md:inline-flex items-center gap-2 bg-[#00E5FF] text-black font-bold px-5 py-2.5 rounded-sm hover:bg-[#4DEDFF] transition-colors cta-glow"
                        >
                            Get Free Quote <ArrowRight className="h-4 w-4" />
                        </button>
                        <button
                            data-testid="mobile-menu-toggle"
                            className="lg:hidden text-white p-2"
                            aria-label={mobileOpen ? "Close menu" : "Open menu"}
                            aria-expanded={mobileOpen}
                            aria-controls="mobile-navigation"
                            onClick={() => setMobileOpen((o) => !o)}
                        >
                            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                        </button>
                    </div>
                </div>
                {mobileOpen && (
                    <div id="mobile-navigation" data-testid="mobile-menu" className="lg:hidden border-t border-white/10 bg-black/95">
                        <div className="px-6 py-4 flex flex-col gap-4 text-zinc-300">
                            <button data-testid="mobile-nav-services" onClick={scrollTo("services")} className="text-left">Services</button>
                            <button data-testid="mobile-nav-gallery" onClick={scrollTo("repair-gallery")} className="text-left">Repair Gallery</button>
                            <button data-testid="mobile-nav-why" onClick={scrollTo("why")} className="text-left">Why Us</button>
                            <button data-testid="mobile-nav-reviews" onClick={scrollTo("reviews")} className="text-left">Reviews</button>
                            <button data-testid="mobile-nav-faq" onClick={scrollTo("faq")} className="text-left">FAQ</button>
                            <button data-testid="mobile-nav-contact" onClick={scrollTo("contact")} className="text-left">Contact</button>
                            <div className="grid grid-cols-2 gap-2 pt-2">
                                <a
                                    href="tel:+441738631750"
                                    data-testid="menu-call-btn"
                                    className="inline-flex items-center justify-center gap-2 border border-white/20 text-white px-4 py-3 rounded-sm text-sm font-medium"
                                >
                                    <Phone className="h-4 w-4" /> Call
                                </a>
                                <a
                                    href="https://wa.me/447402883213"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="menu-whatsapp-btn"
                                    className="inline-flex items-center justify-center gap-2 border border-white/20 text-white px-4 py-3 rounded-sm text-sm font-medium"
                                >
                                    <MessageCircle className="h-4 w-4" /> WhatsApp
                                </a>
                            </div>
                            <button
                                data-testid="mobile-menu-quote-cta"
                                onClick={scrollTo("contact")}
                                className="mt-1 bg-[#00E5FF] text-black font-bold px-5 py-3 rounded-sm"
                            >
                                Get Free Quote
                            </button>
                        </div>
                    </div>
                )}
            </header>

            {/* Hero */}
            <section
                id="hero"
                data-testid="hero-section"
                className="relative pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden"
            >
                <div className="absolute inset-0 grid-pattern opacity-30" />
                <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                    <motion.div
                        initial={{ opacity: 0, y: reduceMotion ? 0 : 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="lg:col-span-7"
                    >
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/15 bg-white/5 text-xs uppercase tracking-[0.2em] text-[#00E5FF] mb-8">
                            <span className="h-1.5 w-1.5 rounded-full bg-[#00E5FF] animate-pulse" />
                            Same-day repairs • 90-day warranty
                        </div>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.06]" data-testid="hero-heading">
                            {["Your phone,", "back to its best."].map((line, index) => (
                                <span key={line} className="block overflow-hidden pb-1">
                                    <motion.span className={`block ${index ? "text-[#00E5FF]" : ""}`} initial={{ y: reduceMotion ? 0 : "110%" }} animate={{ y: 0 }} transition={{ duration: 0.85, delay: 0.1 + index * 0.15, ease: [0.22, 1, 0.36, 1] }}>{line}</motion.span>
                                </span>
                            ))}
                        </h1>
                        <p className="mt-8 text-base md:text-lg text-zinc-400 max-w-xl leading-relaxed">
                            Certified technicians. OEM-grade parts. Transparent pricing.
                            Mr Mobile gets you back in business while you grab a coffee.
                        </p>
                        <div className="mt-10 flex flex-col sm:flex-row gap-4">
                            <button
                                data-testid="hero-quote-cta"
                                onClick={scrollTo("contact")}
                                className="cta-glow inline-flex items-center justify-center gap-2 bg-[#00E5FF] text-black font-bold px-8 py-4 rounded-sm hover:bg-[#4DEDFF]"
                            >
                                Get Free Quote <ArrowRight className="h-4 w-4" />
                            </button>
                            <button
                                data-testid="hero-services-cta"
                                onClick={scrollTo("services")}
                                className="inline-flex items-center justify-center gap-2 border border-white/20 text-white font-medium px-8 py-4 rounded-sm hover:border-white/60 hover:bg-white/5 transition-all"
                            >
                                Explore Services
                            </button>
                        </div>
                        <div className="mt-14 grid grid-cols-3 gap-6 max-w-lg">
                            {[
                                { k: "50K+", v: "Repairs done" },
                                { k: "4.9★", v: "Avg rating" },
                                { k: "30 min", v: "Avg turnaround" },
                            ].map((s) => (
                                <div key={s.k} data-testid={`hero-stat-${s.v.replace(/\s+/g, "-")}`}>
                                    <div className="font-display text-2xl md:text-3xl font-bold text-white">{s.k}</div>
                                    <div className="text-xs text-zinc-500 mt-1">{s.v}</div>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                    <div className="lg:col-span-5 repair-hero-stack">
                        <RepairHeroPhoto />
                    </div>
                </div>
            </section>

            {/* Brands marquee */}
            <section data-testid="brands-section" className="py-12 border-y border-white/10 bg-[#0A0A0F] overflow-hidden">
                <div className="max-w-7xl mx-auto px-6 md:px-12 mb-6">
                    <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Trusted with every major brand</div>
                </div>
                <div className="relative w-full overflow-hidden">
                    <div className="flex gap-16 animate-marquee whitespace-nowrap">
                        {[...BRANDS, ...BRANDS].map((b, i) => (
                            <span
                                key={`${b}-${i}`}
                                className="font-display text-3xl md:text-4xl font-semibold text-zinc-600 hover:text-white transition-colors"
                            >
                                {b}
                            </span>
                        ))}
                    </div>
                </div>
            </section>

            <RepairShowcase onQuote={scrollTo("contact")} />

            {/* Services */}
            <section id="services" data-testid="services-section" className="py-16 md:py-32">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
                        <div className="max-w-2xl">
                            <div className="text-xs uppercase tracking-[0.2em] text-[#00E5FF]">What we fix</div>
                            <h2 className="font-display text-3xl md:text-5xl font-semibold tracking-tight mt-4">
                                Every issue. Every device. <span className="text-zinc-500">Done right.</span>
                            </h2>
                        </div>
                        <p className="text-zinc-400 max-w-md">
                            From cracked glass to drowned boards, our master techs have seen — and fixed — it all.
                        </p>
                    </div>

                    {/* Bento grid */}
                    <div className="grid grid-cols-1 md:grid-cols-6 gap-4 md:gap-6">
                        {/* Feature card */}
                        <div
                            data-testid="service-card-feature"
                            className="bento-hover md:col-span-4 md:row-span-2 relative overflow-hidden rounded-2xl md:rounded-3xl border border-white/10 bg-[#0A0A0F] p-8 md:p-12 min-h-[420px]"
                        >
                            <img src={SCREEN_IMG} alt="" loading="lazy" decoding="async" data-testid="service-phone-parts-image" className="absolute inset-0 w-full h-full object-cover opacity-30" />
                            <div className="relative z-10 flex flex-col h-full">
                                <div className="flex items-center gap-3 text-[#00E5FF]">
                                    <Smartphone className="h-5 w-5" />
                                    <span className="text-xs uppercase tracking-[0.2em]">Most popular</span>
                                </div>
                                <h3 className="font-display text-3xl md:text-4xl font-semibold mt-6 max-w-md">
                                    Screen Replacement — back in your hands in 30 minutes.
                                </h3>
                                <p className="text-zinc-400 mt-4 max-w-md">
                                    Walk-in service for iPhone, Samsung, Pixel, OnePlus and more.
                                    OEM-grade displays with original touch response and brightness.
                                </p>
                                <div className="mt-auto pt-10 flex flex-wrap items-center gap-6 text-sm">
                                    <span className="text-white font-semibold">30-minute turnaround</span>
                                    <span className="text-zinc-500">•</span>
                                    <span className="text-zinc-400">Walk-in welcome</span>
                                    <span className="text-zinc-500">•</span>
                                    <span className="text-zinc-400">Lifetime guarantee</span>
                                </div>
                            </div>
                        </div>

                        {SERVICES.slice(1).map((s) => {
                            const Icon = s.icon;
                            return (
                                <div
                                    key={s.title}
                                    data-testid={`service-card-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
                                    className="bento-hover md:col-span-2 rounded-2xl border border-white/10 bg-[#0A0A0F] p-6 md:p-8 min-h-[200px] flex flex-col"
                                >
                                    <div className="h-10 w-10 rounded-md bg-[#00E5FF]/10 text-[#00E5FF] flex items-center justify-center">
                                        <Icon className="h-5 w-5" />
                                    </div>
                                    <h3 className="font-display text-lg md:text-xl font-medium mt-5">{s.title}</h3>
                                    <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{s.desc}</p>
                                    <div className="mt-auto pt-4 flex items-center justify-end text-xs text-zinc-500">
                                        <span>{s.time}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Why us */}
            <section id="why" data-testid="why-section" className="py-16 md:py-32 bg-[#08080C] border-y border-white/5">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-5">
                        <div className="text-xs uppercase tracking-[0.2em] text-[#00E5FF]">Why Mr Mobile</div>
                        <h2 className="font-display text-3xl md:text-5xl font-semibold tracking-tight mt-4 leading-tight">
                            Repairs you can <span className="text-[#00E5FF]">trust.</span> Speed you can feel.
                        </h2>
                        <p className="text-zinc-400 mt-6 leading-relaxed">
                            Every device passes a 12-point diagnostic before and after repair.
                            Transparent pricing, no hidden fees, and a 90-day warranty on every job.
                        </p>
                        <figure className="mt-10" data-testid="phone-inspection-figure">
                            <div className="aspect-[4/3] overflow-hidden border border-white/10">
                                <img src={INSPECTION_IMG} alt="Microscope inspection of opened mobile phones on a repair mat" loading="lazy" decoding="async" width="1200" height="800" data-testid="why-phone-image" className="h-full w-full object-cover" />
                            </div>
                            <figcaption className="mt-3 text-[11px] text-zinc-500" data-testid="why-photo-disclaimer">Mobile phone repair · Illustrative stock photography</figcaption>
                        </figure>
                    </div>
                    <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4 md:gap-6">
                        {[
                            { icon: ShieldCheck, t: "90-Day Warranty", d: "Every repair backed by parts & labour cover. Lifetime on screens." },
                            { icon: BadgeCheck, t: "Certified Techs", d: "Apple & Samsung trained. 10+ years average industry experience." },
                            { icon: Zap, t: "Same-Day Service", d: "Most repairs walk-in to walk-out in under 45 minutes." },
                            { icon: Wrench, t: "OEM-Grade Parts", d: "Original or premium aftermarket — clearly labelled, no surprises." },
                        ].map((b) => {
                            const I = b.icon;
                            return (
                                <div
                                    key={b.t}
                                    data-testid={`benefit-${b.t.toLowerCase().replace(/\s+/g, "-")}`}
                                    className="bento-hover rounded-2xl border border-white/10 bg-[#0A0A0F] p-8 min-h-[200px]"
                                >
                                    <div className="h-11 w-11 rounded-md bg-[#00E5FF]/10 text-[#00E5FF] flex items-center justify-center">
                                        <I className="h-5 w-5" />
                                    </div>
                                    <h3 className="font-display text-xl font-medium mt-6">{b.t}</h3>
                                    <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{b.d}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section id="reviews" data-testid="reviews-section" className="py-16 md:py-32">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="text-xs uppercase tracking-[0.2em] text-[#00E5FF]">Customer love</div>
                    <h2 className="font-display text-3xl md:text-5xl font-semibold tracking-tight mt-4 max-w-2xl">
                        Real customers. Honest reviews.
                    </h2>
                    <div className="mt-14 grid md:grid-cols-3 gap-4 md:gap-6">
                        {TESTIMONIALS.map((t) => (
                            <div
                                key={t.name}
                                data-testid={`review-${t.name.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                                className="bento-hover rounded-2xl border border-white/10 bg-[#0A0A0F] p-8 min-h-[240px] flex flex-col"
                            >
                                <div className="flex gap-1 text-[#E5FE40]">
                                    {[...Array(5)].map((_, i) => (
                                        <Star key={i} className="h-4 w-4 fill-current" />
                                    ))}
                                </div>
                                <p className="text-zinc-200 mt-6 leading-relaxed">&ldquo;{t.text}&rdquo;</p>
                                <div className="mt-auto pt-6">
                                    <div className="font-display font-medium text-white">{t.name}</div>
                                    <div className="text-xs text-zinc-500 mt-0.5">{t.role}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Quote form */}
            <section id="contact" data-testid="contact-section" className="py-16 md:py-32 bg-[#08080C] border-y border-white/5">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <div className="text-xs uppercase tracking-[0.2em] text-[#00E5FF]">Free quote</div>
                        <h2 className="font-display text-3xl md:text-5xl font-semibold tracking-tight mt-4 leading-tight">
                            Tell us what&rsquo;s broken.
                        </h2>
                        <p className="text-zinc-400 mt-6 leading-relaxed max-w-md">
                            Free diagnostic, transparent quote within 30 minutes.
                            No-fix, no-fee guarantee.
                        </p>
                        <div className="mt-10 space-y-5">
                            <div className="flex items-center gap-4 text-sm text-zinc-300">
                                <MapPin className="h-5 w-5 text-[#00E5FF]" /> 135 South Street, Perth PH2 8PA
                            </div>
                            <div className="flex items-center gap-4 text-sm text-zinc-300">
                                <Phone className="h-5 w-5 text-[#00E5FF]" />
                                <a href="tel:+441738631750" data-testid="contact-phone" className="hover:text-white transition-colors">01738 631750</a>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-zinc-300">
                                <MessageCircle className="h-5 w-5 text-[#00E5FF]" />
                                <a href="https://wa.me/447402883213" target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp" className="hover:text-white transition-colors">WhatsApp 07402 883213</a>
                            </div>
                            <div className="flex items-start gap-4 text-sm text-zinc-300">
                                <Clock className="h-5 w-5 text-[#00E5FF] mt-0.5 shrink-0" />
                                <div>
                                    <div data-testid="contact-weekday-hours">Mon–Sat 9:00am – 5:30pm</div>
                                    <div data-testid="contact-sunday-hours">Sunday Closed</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        <form
                            onSubmit={onSubmit}
                            data-testid="quote-form"
                            className="rounded-2xl border border-white/10 bg-[#0A0A0F] p-6 md:p-10 space-y-5"
                        >
                            <div className="grid sm:grid-cols-2 gap-5">
                                <div className="space-y-2">
                                    <Label htmlFor="name" className="text-zinc-300">Full name</Label>
                                    <Input
                                        id="name"
                                        data-testid="quote-name"
                                        required
                                        value={form.name}
                                        onChange={update("name")}
                                        placeholder="Jamie Carter"
                                        className="bg-white/5 border-white/10 text-white placeholder:text-zinc-600 focus:border-[#00E5FF] focus-visible:ring-[#00E5FF]"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="phone" className="text-zinc-300">Phone</Label>
                                    <Input
                                        id="phone"
                                        data-testid="quote-phone"
                                        required
                                        value={form.phone}
                                        onChange={update("phone")}
                                        placeholder="+1 555 010 2024"
                                        className="bg-white/5 border-white/10 text-white placeholder:text-zinc-600 focus:border-[#00E5FF] focus-visible:ring-[#00E5FF]"
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="email" className="text-zinc-300">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    data-testid="quote-email"
                                    required
                                    value={form.email}
                                    onChange={update("email")}
                                    placeholder="jamie@example.com"
                                    className="bg-white/5 border-white/10 text-white placeholder:text-zinc-600 focus:border-[#00E5FF] focus-visible:ring-[#00E5FF]"
                                />
                            </div>
                            <div className="grid sm:grid-cols-2 gap-5">
                                <div className="space-y-2">
                                    <Label htmlFor="device" className="text-zinc-300">Device</Label>
                                    <Input
                                        id="device"
                                        data-testid="quote-device"
                                        required
                                        value={form.device}
                                        onChange={update("device")}
                                        placeholder="iPhone 14 Pro"
                                        className="bg-white/5 border-white/10 text-white placeholder:text-zinc-600 focus:border-[#00E5FF] focus-visible:ring-[#00E5FF]"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-zinc-300">Issue</Label>
                                    <Select value={form.issue} onValueChange={(v) => setForm((f) => ({ ...f, issue: v }))}>
                                        <SelectTrigger
                                            data-testid="quote-issue"
                                            className="bg-white/5 border-white/10 text-white focus:border-[#00E5FF] focus-visible:ring-[#00E5FF]"
                                        >
                                            <SelectValue placeholder="Pick a service" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-[#0A0A0F] border-white/10 text-white">
                                            {SERVICES.map((s) => (
                                                <SelectItem key={s.title} value={s.title} data-testid={`quote-issue-${s.title.toLowerCase().replace(/\s+/g, "-")}`}>
                                                    {s.title}
                                                </SelectItem>
                                            ))}
                                            <SelectItem value="Other" data-testid="quote-issue-other">Other / Not sure</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="description" className="text-zinc-300">Describe the problem</Label>
                                <Textarea
                                    id="description"
                                    data-testid="quote-description"
                                    rows={4}
                                    value={form.description}
                                    onChange={update("description")}
                                    placeholder="Dropped it on tiles last night, top right corner is cracked but touch still works…"
                                    className="bg-white/5 border-white/10 text-white placeholder:text-zinc-600 focus:border-[#00E5FF] focus-visible:ring-[#00E5FF]"
                                />
                            </div>
                            <button
                                data-testid="quote-submit"
                                type="submit"
                                disabled={submitting || !form.issue}
                                className="w-full sm:w-auto cta-glow inline-flex items-center justify-center gap-2 bg-[#00E5FF] text-black font-bold px-8 py-4 rounded-sm hover:bg-[#4DEDFF] disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {submitting ? "Sending…" : "Request Free Quote"} <ArrowRight className="h-4 w-4" />
                            </button>
                        </form>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section id="faq" data-testid="faq-section" className="py-16 md:py-32">
                <div className="max-w-3xl mx-auto px-6 md:px-12">
                    <div className="text-xs uppercase tracking-[0.2em] text-[#00E5FF] text-center">Questions, answered</div>
                    <h2 className="font-display text-3xl md:text-5xl font-semibold tracking-tight mt-4 text-center">
                        Frequently asked
                    </h2>
                    <Accordion type="single" collapsible className="mt-14 space-y-3" data-testid="faq-accordion">
                        {FAQS.map((f, i) => (
                            <AccordionItem
                                key={f.q}
                                value={`item-${i}`}
                                data-testid={`faq-item-${i}`}
                                className="border border-white/10 rounded-xl bg-[#0A0A0F] px-6"
                            >
                                <AccordionTrigger className="text-left text-white hover:no-underline py-5">
                                    {f.q}
                                </AccordionTrigger>
                                <AccordionContent className="text-zinc-400 leading-relaxed pb-6">
                                    {f.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>

            {/* Footer */}
            <footer data-testid="site-footer" className="py-16 md:py-20 bg-[#030305] border-t border-white/5">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-4 gap-10">
                    <div>
                        <div className="flex items-center gap-2 font-display font-bold text-xl">
                            <span className="inline-flex h-8 w-8 items-center justify-center rounded-sm bg-[#00E5FF] text-black">
                                <Wrench className="h-4 w-4" />
                            </span>
                            Mr Mobile
                        </div>
                        <p className="text-sm text-zinc-500 mt-4 leading-relaxed">
                            Premium mobile repair in the heart of Perth. Certified techs.
                            Real warranties. Real fast.
                        </p>
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-[0.2em] text-zinc-400">Services</div>
                        <ul className="mt-4 space-y-2 text-sm text-zinc-500">
                            {SERVICES.slice(0, 5).map((s) => (
                                <li key={s.title} className="hover:text-white transition-colors cursor-pointer" onClick={scrollTo("services")}>{s.title}</li>
                            ))}
                        </ul>
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-[0.2em] text-zinc-400">Visit</div>
                        <ul className="mt-4 space-y-2 text-sm text-zinc-500">
                            <li>135 South Street, Perth PH2 8PA</li>
                            <li>01738 631750</li>
                            <li>WhatsApp 07402 883213</li>
                            <li data-testid="footer-weekday-hours">Mon–Sat 9:00am – 5:30pm</li>
                            <li data-testid="footer-sunday-hours">Sunday Closed</li>
                        </ul>
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-[0.2em] text-zinc-400">Company</div>
                        <ul className="mt-4 space-y-2 text-sm text-zinc-500">
                            <li><button data-testid="footer-gallery-link" onClick={scrollTo("repair-gallery")} className="hover:text-white transition-colors">Repair Gallery</button></li>
                            <li><button data-testid="footer-about-link" onClick={scrollTo("why")} className="hover:text-white transition-colors">About</button></li>
                            <li><button onClick={scrollTo("reviews")} className="hover:text-white transition-colors">Reviews</button></li>
                            <li><button onClick={scrollTo("faq")} className="hover:text-white transition-colors">FAQ</button></li>
                            <li><Link to="/admin/login" data-testid="footer-admin-link" className="hover:text-white transition-colors">Staff Login</Link></li>
                        </ul>
                    </div>
                </div>
                <div className="max-w-7xl mx-auto px-6 md:px-12 mt-14 pt-8 border-t border-white/5 text-xs text-zinc-600 flex flex-col sm:flex-row justify-between gap-2">
                    <span>© {new Date().getFullYear()} Mr Mobile, Perth. All rights reserved.</span>
                    <span>Crafted for the people who hate broken phones.</span>
                </div>
            </footer>

            {/* Sticky mobile action bar */}
            <div
                data-testid="mobile-action-bar"
                className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-black/90 backdrop-blur-xl border-t border-white/10"
                style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
            >
                <div className="grid grid-cols-3 gap-1 p-2">
                    <a
                        href="tel:+441738631750"
                        data-testid="sticky-call-btn"
                        className="inline-flex flex-col items-center justify-center gap-0.5 py-2 rounded-sm text-white active:bg-white/10"
                    >
                        <Phone className="h-5 w-5 text-[#00E5FF]" />
                        <span className="text-[11px] font-medium">Call</span>
                    </a>
                    <a
                        href="https://wa.me/447402883213"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="sticky-whatsapp-btn"
                        className="inline-flex flex-col items-center justify-center gap-0.5 py-2 rounded-sm text-white active:bg-white/10"
                    >
                        <MessageCircle className="h-5 w-5 text-[#00E5FF]" />
                        <span className="text-[11px] font-medium">WhatsApp</span>
                    </a>
                    <button
                        onClick={scrollTo("contact")}
                        data-testid="sticky-quote-btn"
                        className="inline-flex flex-col items-center justify-center gap-0.5 py-2 rounded-sm bg-[#00E5FF] text-black font-bold active:opacity-90"
                    >
                        <ArrowRight className="h-5 w-5" />
                        <span className="text-[11px]">Free Quote</span>
                    </button>
                </div>
            </div>
        </div>
    );
}
